import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, map, of, finalize } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PostData, PostsResponse, PostContentAttachmentLink, UnreadPostsCount, Response } from '../interfaces';
import { SHOW_LOADER } from '../interceptors/loader.interceptor';

@Injectable({
  providedIn: 'root'
})
export class PostsService {
  postsList$: BehaviorSubject<PostData[]> = new BehaviorSubject<PostData[]>([]);
  unreadPostsCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  currentPage = 0;
  totalPages = 1;
  pageSize = 10;
  isLoading = false;

  constructor(private readonly http: HttpClient) {}

  refreshPosts(isShowLoader = true): Observable<PostData[]> {
    this.currentPage = 0;
    this.totalPages = 1;
    //this.postsList$.next([]);
    return this.loadMorePosts(isShowLoader);
  }

  loadMorePosts(isShowLoader = false): Observable<PostData[]> {
    if (this.isLoading || this.currentPage >= this.totalPages) {
      return of(this.postsList$.value ?? []);
    }

    this.isLoading = true;
    const nextPage = this.currentPage + 1;

    return this.getPostsListRequest(nextPage, isShowLoader).pipe(
      tap((response) => {
        if (response.success && response.data?.content) {
          let updatedList: PostData[];
          if (this.currentPage === 0) {
            updatedList = response.data.content;
          } else {
            updatedList = [...(this.postsList$.value || []), ...response.data.content];
          }
          this.postsList$.next(updatedList);

          this.currentPage = response.data.currentPage;
          this.totalPages = response.data.totalPages;
        }
      }),
      map((response) => response?.data?.content ?? []),
      finalize(() => (this.isLoading = false))
    );
  }

  getUnreadPostsCount() {
    this.getUnreadPostsCountRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.unreadPostsCount$.next(response.data.unviewedPostsCount);
        }
      }
    });
  }

  changePostItemState(ids: number[] | null, viewed: boolean) {
    this.togglePostStateRequest(ids, viewed).subscribe({
      next: (response) => {
        if (!response.success) return;

        const currentPostsList = this.postsList$.getValue();
        if (!currentPostsList) return;

        if (ids) {
          const delta = viewed ? -1 : 1;
          this.unreadPostsCount$.next(Math.max((this.unreadPostsCount$.value ?? 0) + delta, 0));
        } else {
          this.unreadPostsCount$.next(0);
        }

        const updatedList = currentPostsList.map((item) => (!ids || ids.includes(item.id) ? { ...item, viewed } : item));

        this.postsList$?.next(updatedList);
      }
    });
  }

  createPostRequest(content: string, attachments: File[]): Observable<Response<PostData>> {
    const formData = new FormData();

    for (const attachment of attachments) {
      if (attachment?.name) {
        formData.append('attachments', attachment, attachment.name);
      }
    }

    formData.append('request', new Blob([JSON.stringify({ content })], { type: 'application/json' }));

    return this.http.post<Response<PostData>>(`${environment.baseUrl}/posts`, formData);
  }

  private getPostsListRequest(nextPage: number, isShowLoader: boolean): Observable<Response<PostsResponse>> {
    return this.http.get<Response<PostsResponse>>(`${environment.baseUrl}/posts?page=${nextPage}&size=${this.pageSize}`, {
      context: new HttpContext().set(SHOW_LOADER, isShowLoader)
    });
  }

  getPostByIdRequest(id: number): Observable<Response<PostData>> {
    return this.http.get<Response<PostData>>(`${environment.baseUrl}/posts/${id}`, {
      context: new HttpContext().set(SHOW_LOADER, false)
    });
  }

  getUnreadPostsCountRequest(): Observable<Response<UnreadPostsCount>> {
    return this.http.get<Response<UnreadPostsCount>>(`${environment.baseUrl}/posts/unviewed`);
  }

  updatePostRequest(id: number, content: string, attachments: File[]): Observable<Response<PostData>> {
    const formData = new FormData();

    for (const attachment of attachments) {
      if (attachment?.name) {
        formData.append('attachments', attachment, attachment.name);
      }
    }

    formData.append('request', new Blob([JSON.stringify({ content })], { type: 'application/json' }));

    return this.http.put<Response<PostData>>(`${environment.baseUrl}/posts/${id}`, formData);
  }

  uploadImagesToContentRequest(attachment: File): Observable<Response<PostContentAttachmentLink>> {
    const formData = new FormData();

    if (attachment?.name) {
      formData.append('attachment', attachment, attachment.name);
    }

    return this.http.post<Response<PostContentAttachmentLink>>(`${environment.baseUrl}/posts/attachment`, formData);
  }

  removePostRequest(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/posts/${id}`);
  }

  removeAttachmentRequest(postId: number, id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/posts/${postId}/attachment/${id}`);
  }

  toggleReactionRequest(postId: number, emojiId: string): Observable<Response<any>> {
    const params = new HttpParams().set('emojiId', emojiId);
    return this.http.post<Response<any>>(
      `${environment.baseUrl}/posts/${postId}/toggle-reaction`,
      {},
      {
        params: params,
        context: new HttpContext().set(SHOW_LOADER, false)
      }
    );
  }

  togglePostStateRequest(postIds: number[] | null, viewed: boolean): Observable<Response<any>> {
    return this.http.post<Response<any>>(
      `${environment.baseUrl}/posts/set-viewed`,
      { postIds: postIds, viewed: viewed },
      {
        context: new HttpContext().set(SHOW_LOADER, false)
      }
    );
  }

  clearData() {
    this.postsList$.next([]);
  }
}
